import {api} from '../../../shared/api';
import {
  AppealMaterialModel,
  ApplicationDetailModel,
  ApplicationListItemModel,
  ApplicationMediaModel,
  mapMediaKind,
  MediaKind,
  PagedView,
} from '../model';

const BASE = '/api/fundraising/representative/applications';
const ADMIN_BASE = '/api/fundraising/admin/applications';

interface ApplicationListDTO {
  appealId: string;
  appealName: string;
  mediaCount: number;
  hasUnreadMessages?: boolean;
}

interface ApplicationMediaDTO {
  id: string;
  kind: string;
  fileName: string;
  url: string;
  thumbnailUrl?: string;
  caption?: string;
}

interface AppealMaterialDTO {
  id: string;
  kind: string;
  fileName: string;
  storageKey: string;
  url: string;
  thumbnailUrl?: string | null;
  thumbnailStorageKey?: string | null;
  caption?: string | null;
}

export const applicationApi = {
  getMyApplications: async (params: {after?: string; pageSize?: number} = {}): Promise<PagedView<ApplicationListItemModel>> => {
    const {pageSize = 20, after} = params;
    const queryParams: Record<string, unknown> = {first: pageSize, includeTotalCount: true};
    if (after) queryParams.after = after;

    const {data} = await api.get<PagedView<ApplicationListDTO>>(BASE, {params: queryParams});
    return {
      ...data,
      items: data.items.map((item) => ({
        appealId: item.appealId,
        appealName: item.appealName,
        mediaCount: item.mediaCount,
        hasUnreadMessages: item.hasUnreadMessages ?? false,
      })),
    };
  },

  getMyApplication: async (appealId: string): Promise<ApplicationDetailModel> => {
    const {data} = await api.get<ApplicationDetailModel>(`${BASE}/${appealId}`);
    return {appealId: data.appealId, appealName: data.appealName};
  },

  getApplicationMedia: async (appealId: string, params: {after?: string; pageSize?: number} = {}): Promise<PagedView<ApplicationMediaModel>> => {
    const {pageSize = 50, after} = params;
    const queryParams: Record<string, unknown> = {first: pageSize, includeTotalCount: true};
    if (after) queryParams.after = after;

    const {data} = await api.get<PagedView<ApplicationMediaDTO>>(`${BASE}/${appealId}/media`, {params: queryParams});
    return {
      ...data,
      items: data.items.map((item) => ({
        id: item.id,
        kind: mapMediaKind(item.kind),
        fileName: item.fileName,
        url: item.url,
        thumbnailUrl: item.thumbnailUrl,
        caption: item.caption,
      })),
    };
  },

  addMedia: async (
    appealId: string,
    uploadId: string,
    kind: MediaKind,
    caption?: string,
  ): Promise<ApplicationMediaModel> => {
    const body: {uploadId: string; kind: string; caption?: string} = {uploadId, kind};
    if (caption !== undefined) body.caption = caption;

    const {data} = await api.post<ApplicationMediaDTO>(`${BASE}/${appealId}/media`, body);
    return {
      id: data.id,
      kind: mapMediaKind(data.kind),
      fileName: data.fileName,
      url: data.url,
      thumbnailUrl: data.thumbnailUrl,
      caption: data.caption,
    };
  },

  deleteMedia: async (appealId: string, mediaId: string): Promise<void> => {
    await api.delete<void>(`${BASE}/${appealId}/media/${mediaId}`);
  },

  // Бэкенд отвечает 204 No Content — тело не парсим.
  updateCaption: async (appealId: string, mediaId: string, caption: string): Promise<void> => {
    await api.put<void>(`${BASE}/${appealId}/media/${mediaId}`, {caption});
  },

  getAppealMaterials: async (appealId: string, params: {after?: string; pageSize?: number} = {}): Promise<PagedView<AppealMaterialModel>> => {
    const {pageSize = 50, after} = params;
    const queryParams: Record<string, unknown> = {first: pageSize, includeTotalCount: true};
    if (after) queryParams.after = after;

    const {data} = await api.get<PagedView<AppealMaterialDTO>>(`${ADMIN_BASE}/${appealId}/media`, {params: queryParams});
    return {
      ...data,
      items: data.items.map((item) => ({
        id: item.id,
        storageKey: item.storageKey,
        kind: mapMediaKind(item.kind),
        fileName: item.fileName,
        url: item.url,
        thumbnailUrl: item.thumbnailUrl ?? null,
        thumbnailStorageKey: item.thumbnailStorageKey ?? null,
        caption: item.caption ?? null,
      })),
    };
  },

  addAppealMaterial: async (
    appealId: string,
    uploadId: string,
    kind: MediaKind,
    caption?: string,
  ): Promise<AppealMaterialModel> => {
    const body: {uploadId: string; kind: string; caption?: string} = {uploadId, kind};
    if (caption !== undefined) body.caption = caption;

    const {data} = await api.post<AppealMaterialDTO>(`${ADMIN_BASE}/${appealId}/media`, body);
    return {
      id: data.id,
      storageKey: data.storageKey,
      kind: mapMediaKind(data.kind),
      fileName: data.fileName,
      url: data.url,
      thumbnailUrl: data.thumbnailUrl ?? null,
      thumbnailStorageKey: data.thumbnailStorageKey ?? null,
      caption: data.caption ?? null,
    };
  },

  deleteAppealMaterial: async (appealId: string, materialId: string): Promise<void> => {
    await api.delete<void>(`${ADMIN_BASE}/${appealId}/media/${materialId}`);
  },

  // Бэкенд отвечает 204 No Content — тело не парсим.
  updateAppealMaterialCaption: async (appealId: string, materialId: string, caption: string): Promise<void> => {
    await api.put<void>(`${ADMIN_BASE}/${appealId}/media/${materialId}`, {caption});
  },
};
